import { Form, Input, Modal, Select } from "antd";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createCandidate, updateCandidate, CANDIDATE_STAGES, getJobList, stageIdToKey } from "../../redux/rtk/features/hiring/hiringSlice";

const SOURCE_OPTIONS = [
	{ value: "Referral", label: "Referral" },
	{ value: "LinkedIn", label: "LinkedIn" },
	{ value: "Job portal", label: "Job portal" },
	{ value: "Company website", label: "Company website" },
	{ value: "Other", label: "Other" },
];

const HiringCandidateForm = ({ open, onClose, onSuccess, initialValues, jobList: jobListProp }) => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const { loading, jobList } = useSelector((state) => state.hiring);
	const jobs = jobListProp ?? jobList ?? [];

	const isEdit = Boolean(initialValues && (initialValues.CandidateId ?? initialValues.id));

	useEffect(() => {
		if (open && !jobListProp) {
			dispatch(getJobList());
		}
	}, [open, dispatch, jobListProp]);

	useEffect(() => {
		if (open && initialValues) {
			form.setFieldsValue({
				firstName: initialValues.FirstName ?? initialValues.firstName ?? "",
				middleName: initialValues.MiddleName ?? initialValues.middleName ?? "",
				lastName: initialValues.LastName ?? initialValues.lastName ?? "",
				email: initialValues.Email ?? initialValues.email ?? "",
				phone: initialValues.Phone ?? initialValues.phone ?? "",
				jobOpeningId: initialValues.JobOpeningId ?? initialValues.jobOpeningId ?? initialValues.jobId ?? initialValues.JobId ?? undefined,
				source: initialValues.Source ?? initialValues.source ?? undefined,
				stage: initialValues.StageId != null ? stageIdToKey(initialValues.StageId) : (initialValues.stage ?? initialValues.Stage ?? "APPLIED"),
				remarks: initialValues.Remarks ?? initialValues.remarks ?? "",
			});
		} else if (open && !initialValues) {
			form.resetFields();
			form.setFieldsValue({ stage: "APPLIED" });
		}
	}, [open, initialValues, form]);

	const jobOptions = (Array.isArray(jobs) ? jobs : []).map((j) => ({
		value: j.JobOpeningId ?? j.id ?? j.JobId,
		label: j.Title ?? j.title ?? j.JobTitle ?? j.jobTitle ?? `Job #${j.JobOpeningId ?? j.id ?? j.JobId}`,
	}));

	const stageOptions = CANDIDATE_STAGES.map((s) => ({ value: s.key, label: s.label }));

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			const payload = {
				firstName: values.firstName,
				middleName: values.middleName,
				lastName: values.lastName,
				email: values.email,
				phone: values.phone,
				jobId: values.jobOpeningId,
				source: values.source,
				stage: values.stage ?? "APPLIED",
				remarks: values.remarks,
			};
			if (isEdit) {
				const id = initialValues.CandidateId ?? initialValues.id;
				dispatch(updateCandidate({ id, ...payload }))
					.unwrap()
					.then(() => {
						onSuccess?.();
						onClose?.();
					})
					.catch(() => {});
			} else {
				dispatch(createCandidate(payload))
					.unwrap()
					.then(() => {
						onSuccess?.();
						onClose?.();
					})
					.catch(() => {});
			}
		});
	};

	return (
		<Modal
			title={isEdit ? "Edit candidate" : "Add candidate"}
			open={open}
			onCancel={onClose}
			onOk={handleSubmit}
			confirmLoading={loading}
			destroyOnClose
			width={520}
		>
			<Form form={form} layout="vertical">
				<Form.Item
					name="firstName"
					label="First name"
					rules={[{ required: true, message: "Please enter first name" }]}
				>
					<Input placeholder="First name" />
				</Form.Item>
				<Form.Item name="middleName" label="Middle name">
					<Input placeholder="Middle name (optional)" />
				</Form.Item>
				<Form.Item
					name="lastName"
					label="Last name"
					rules={[{ required: true, message: "Please enter last name" }]}
				>
					<Input placeholder="Last name" />
				</Form.Item>
				<Form.Item
					name="email"
					label="Email"
					rules={[{ type: "email", message: "Invalid email" }]}
				>
					<Input placeholder="email@example.com" />
				</Form.Item>
				<Form.Item name="phone" label="Phone">
					<Input placeholder="Phone number" />
				</Form.Item>
				<Form.Item name="jobOpeningId" label="Job opening">
					<Select
						placeholder="Select job"
						allowClear
						options={jobOptions}
						showSearch
						optionFilterProp="label"
					/>
				</Form.Item>
				<Form.Item name="source" label="Source">
					<Select placeholder="Select source" allowClear options={SOURCE_OPTIONS} />
				</Form.Item>
				<Form.Item name="stage" label="Stage" initialValue="APPLIED">
					<Select options={stageOptions} />
				</Form.Item>
				<Form.Item name="remarks" label="Remarks">
					<Input.TextArea rows={2} placeholder="Notes" />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default HiringCandidateForm;
